import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Grid,
    useTheme,
    IconButton,
    Tabs,
    Tab,
    Checkbox,
    Tooltip,
    Dialog as ConfirmDialog,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { noticesService, Notice, NewNotice, UpdateNotice, NoticeType, NoticeStatus, NoticeContentJSON, BreachNoticeContent, ErasureNoticeContent, InformationalNoticeContent } from '../../services/notices-service';
import { translationService } from '../../services/translation-service';
import { languageService, Language } from '../../services/language-service';
import AIIcon from '../../assets/images/Artificial_intelligence.png';

interface NoticeModalProps {
    open: boolean;
    onClose: () => void;
    onSave: () => void;
    noticeId?: number; // If provided, we're editing; otherwise, creating
}

const NoticeModal: React.FC<NoticeModalProps> = ({ open, onClose, onSave, noticeId }) => {
    const theme = useTheme();
    const isEditMode = !!noticeId;

    // Tab state
    const [currentTab, setCurrentTab] = useState(0);

    // Basic Information state
    const [noticeType, setNoticeType] = useState<NoticeType>('INFORMATIONAL_NOTICE');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState<NoticeStatus>('ACTIVE');
    
    // Content JSON state (multi-language)
    const [contentData, setContentData] = useState<NoticeContentJSON>({
        en: {
            title: '',
            lawfulGround: '',
            information: ''
        }
    });

    // Languages
    const [languages, setLanguages] = useState<Language[]>([]);
    const [activeLanguage, setActiveLanguage] = useState<string>('en');
    const [showStartTranslation, setShowStartTranslation] = useState(false);
    const [acceptedTranslations, setAcceptedTranslations] = useState<Set<string>>(new Set(['en']));
    const [originalTranslations, setOriginalTranslations] = useState<Record<string, NoticeContentJSON>>({});

    // Data loading
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Validation
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    // Notice type change confirmation
    const [pendingNoticeType, setPendingNoticeType] = useState<NoticeType | null>(null);
    const [showTypeChangeDialog, setShowTypeChangeDialog] = useState(false);

    // Load languages on mount
    useEffect(() => {
        if (open) {
            languageService.getSupportedLanguages()
                .then(langs => {
                    setLanguages(langs);
                    const firstNonEnglishLang = langs.find(lang => lang.code !== 'en');
                    if (firstNonEnglishLang) {
                        setActiveLanguage(firstNonEnglishLang.code);
                    }
                })
                .catch(err => {
                    console.error('Failed to load languages:', err);
                });
        }
    }, [open]);

    // Load notice data if editing
    useEffect(() => {
        if (open && noticeId) {
            loadNotice();
        } else if (open) {
            resetForm();
        }
    }, [open, noticeId]);

    // Check if content has been entered
    const hasContent = (): boolean => {
        const englishContent = contentData['en'];
        if (!englishContent) return false;
        
        if (noticeType === 'BREACH_NOTICE') {
            const breach = englishContent as unknown as BreachNoticeContent;
            return !!(breach.title || breach.affectedData || breach.consequences || 
                    breach.measuresTaken || breach.recommendedPrecautions || breach.contactInfo);
        } else if (noticeType === 'ERASURE_NOTICE') {
            const erasure = englishContent as unknown as ErasureNoticeContent;
            return !!(erasure.title || erasure.dataToBeErased || erasure.reason || erasure.retentionPeriod);
        } else {
            const info = englishContent as unknown as InformationalNoticeContent;
            return !!(info.title || info.lawfulGround || info.information);
        }
    };

    // Check if translations exist
    const hasTranslations = (): boolean => {
        return Object.keys(contentData).length > 1 || 
               Object.keys(originalTranslations).length > 1 ||
               acceptedTranslations.size > 1;
    };

    const handleNoticeTypeChange = (newType: NoticeType) => {
        // If editing, don't allow type change
        if (isEditMode) {
            return;
        }

        // If no content entered yet, allow change
        if (!hasContent() && !hasTranslations()) {
            setNoticeType(newType);
            return;
        }

        // Content exists - show warning
        setPendingNoticeType(newType);
        setShowTypeChangeDialog(true);
    };

    const confirmTypeChange = () => {
        if (pendingNoticeType) {
            setNoticeType(pendingNoticeType);
            resetContentForType();
            setOriginalTranslations({});
            setAcceptedTranslations(new Set(['en']));
            setShowStartTranslation(true);
        }
        setShowTypeChangeDialog(false);
        setPendingNoticeType(null);
    };

    const cancelTypeChange = () => {
        setShowTypeChangeDialog(false);
        setPendingNoticeType(null);
    };

    const resetForm = () => {
        setCurrentTab(0);
        setNoticeType('INFORMATIONAL_NOTICE');
        setTitle('');
        setDescription('');
        setStatus('ACTIVE');
        setError(null);
        setValidationErrors({});
        setShowStartTranslation(true);
        setAcceptedTranslations(new Set(['en']));
        resetContentForType();
    };

    const resetContentForType = () => {
        const baseContent: NoticeContentJSON = {
            en: noticeType === 'BREACH_NOTICE' ? {
                title: '',
                affectedData: '',
                consequences: '',
                measuresTaken: '',
                recommendedPrecautions: '',
                contactInfo: ''
            } : noticeType === 'ERASURE_NOTICE' ? {
                title: '',
                dataToBeErased: '',
                reason: '',
                retentionPeriod: ''
            } : {
                title: '',
                lawfulGround: '',
                information: ''
            }
        };
        setContentData(baseContent);
        setOriginalTranslations({});
    };

    const loadNotice = async () => {
        if (!noticeId) return;

        setLoading(true);
        setError(null);

        try {
            const notice = await noticesService.getNoticeById(noticeId);
            
            setNoticeType(notice.NoticeType);
            setTitle(notice.Title);
            setDescription(notice.Description || '');
            setStatus(notice.Status);
            
            // Parse ContentJSON
            const parsedContent = JSON.parse(notice.ContentJSON) as NoticeContentJSON;
            setContentData(parsedContent);
            setOriginalTranslations(JSON.parse(JSON.stringify(parsedContent))); // Deep copy
            
            // Check if translations exist
            const hasTranslations = Object.keys(parsedContent).length > 1;
            setShowStartTranslation(!hasTranslations);
            
            // Set accepted translations
            const accepted = new Set<string>(Object.keys(parsedContent));
            setAcceptedTranslations(accepted);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load notice');
        } finally {
            setLoading(false);
        }
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!title.trim()) {
            errors.title = 'Title is required';
        }

        // Validate English content (required)
        const englishContent = contentData['en'];
        if (!englishContent) {
            errors.englishContent = 'English content is required';
        } else {
            if (!englishContent.title?.trim()) {
                errors.contentTitle = 'Content title is required in English';
            }

            if (noticeType === 'BREACH_NOTICE') {
                const breachContent = englishContent as BreachNoticeContent;
                if (!breachContent.affectedData?.trim()) errors.affectedData = 'Affected data is required in English';
                if (!breachContent.consequences?.trim()) errors.consequences = 'Consequences is required in English';
                if (!breachContent.measuresTaken?.trim()) errors.measuresTaken = 'Measures taken is required in English';
                if (!breachContent.recommendedPrecautions?.trim()) errors.recommendedPrecautions = 'Recommended precautions is required in English';
                if (!breachContent.contactInfo?.trim()) errors.contactInfo = 'Contact information is required in English';
            } else if (noticeType === 'ERASURE_NOTICE') {
                const erasureContent = englishContent as ErasureNoticeContent;
                if (!erasureContent.dataToBeErased?.trim()) errors.dataToBeErased = 'Data to be erased is required in English';
                if (!erasureContent.reason?.trim()) errors.reason = 'Reason is required in English';
                if (!erasureContent.retentionPeriod?.trim()) errors.retentionPeriod = 'Retention period is required in English';
            } else if (noticeType === 'INFORMATIONAL_NOTICE') {
                const infoContent = englishContent as InformationalNoticeContent;
                if (!infoContent.lawfulGround?.trim()) errors.lawfulGround = 'Lawful ground is required in English';
                if (!infoContent.information?.trim()) errors.information = 'Information is required in English';
            }
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            // Switch to the tab with errors
            if (validationErrors.title || validationErrors.description) {
                setCurrentTab(0);
            } else {
                setCurrentTab(1);
            }
            return;
        }

        setSaving(true);
        setError(null);

        try {
            // Ensure content title matches main title for English
            const updatedContent = { ...contentData };
            if (updatedContent['en']) {
                updatedContent['en'] = {
                    ...updatedContent['en'],
                    title: title
                };
            }

            if (isEditMode && noticeId) {
                const update: UpdateNotice = {
                    Title: title,
                    Description: description || null,
                    ContentJSON: updatedContent,
                    Status: status
                };
                await noticesService.updateNotice(noticeId, update);
            } else {
                const newNotice: NewNotice = {
                    NoticeType: noticeType,
                    Title: title,
                    Description: description || null,
                    ContentJSON: updatedContent,
                    Status: status
                };
                await noticesService.createNotice(newNotice);
            }

            onSave();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save notice');
        } finally {
            setSaving(false);
        }
    };

    const handleContentChange = (field: string, value: string) => {
        setContentData(prev => {
            const updated = { ...prev };
            if (!updated['en']) {
                updated['en'] = noticeType === 'BREACH_NOTICE' ? {
                    title: '',
                    affectedData: '',
                    consequences: '',
                    measuresTaken: '',
                    recommendedPrecautions: '',
                    contactInfo: ''
                } : noticeType === 'ERASURE_NOTICE' ? {
                    title: '',
                    dataToBeErased: '',
                    reason: '',
                    retentionPeriod: ''
                } : {
                    title: '',
                    lawfulGround: '',
                    information: ''
                } as any;
            }
            updated['en'] = {
                ...updated['en'],
                [field]: value
            };
            return updated;
        });
    };

    const handleTranslationChange = (field: string, value: string) => {
        setContentData(prev => {
            const updated = { ...prev };
            if (!updated[activeLanguage]) {
                updated[activeLanguage] = noticeType === 'BREACH_NOTICE' ? {
                    title: '',
                    affectedData: '',
                    consequences: '',
                    measuresTaken: '',
                    recommendedPrecautions: '',
                    contactInfo: ''
                } : noticeType === 'ERASURE_NOTICE' ? {
                    title: '',
                    dataToBeErased: '',
                    reason: '',
                    retentionPeriod: ''
                } : {
                    title: '',
                    lawfulGround: '',
                    information: ''
                } as any;
            }
            updated[activeLanguage] = {
                ...updated[activeLanguage],
                [field]: value
            };
            return updated;
        });
    };

    const handleTranslateAI = async () => {
        // Check if English content exists
        const englishContent = contentData['en'];
        if (!englishContent) {
            setError('Please ensure English content is available for translation');
            return;
        }

        if (activeLanguage === 'en') {
            setError('Please select a target language other than English');
            return;
        }

        setIsTranslating(true);
        setError(null);

        try {
            // Build fields object with all content fields based on notice type
            const fields: { [key: string]: string } = {};

            if (noticeType === 'BREACH_NOTICE') {
                const breachContent = englishContent as BreachNoticeContent;
                if (breachContent.title) fields['title'] = breachContent.title;
                if (breachContent.affectedData) fields['affectedData'] = breachContent.affectedData;
                if (breachContent.consequences) fields['consequences'] = breachContent.consequences;
                if (breachContent.measuresTaken) fields['measuresTaken'] = breachContent.measuresTaken;
                if (breachContent.recommendedPrecautions) fields['recommendedPrecautions'] = breachContent.recommendedPrecautions;
                if (breachContent.contactInfo) fields['contactInfo'] = breachContent.contactInfo;
            } else if (noticeType === 'ERASURE_NOTICE') {
                const erasureContent = englishContent as ErasureNoticeContent;
                if (erasureContent.title) fields['title'] = erasureContent.title;
                if (erasureContent.dataToBeErased) fields['dataToBeErased'] = erasureContent.dataToBeErased;
                if (erasureContent.reason) fields['reason'] = erasureContent.reason;
                if (erasureContent.retentionPeriod) fields['retentionPeriod'] = erasureContent.retentionPeriod;
            } else if (noticeType === 'INFORMATIONAL_NOTICE') {
                const infoContent = englishContent as InformationalNoticeContent;
                if (infoContent.title) fields['title'] = infoContent.title;
                if (infoContent.lawfulGround) fields['lawfulGround'] = infoContent.lawfulGround;
                if (infoContent.information) fields['information'] = infoContent.information;
            }

            if (Object.keys(fields).length === 0) {
                setError('No English content available to translate');
                return;
            }

            const result = await translationService.translateBatch(fields, activeLanguage);

            // Update content data with translations
            setContentData(prev => {
                const updated = { ...prev };
                const prevContent = prev[activeLanguage];
                if (noticeType === 'BREACH_NOTICE') {
                    const prevBreach = prevContent as unknown as BreachNoticeContent;
                    updated[activeLanguage] = {
                        title: result['title'] || prevBreach?.title || '',
                        affectedData: result['affectedData'] || prevBreach?.affectedData || '',
                        consequences: result['consequences'] || prevBreach?.consequences || '',
                        measuresTaken: result['measuresTaken'] || prevBreach?.measuresTaken || '',
                        recommendedPrecautions: result['recommendedPrecautions'] || prevBreach?.recommendedPrecautions || '',
                        contactInfo: result['contactInfo'] || prevBreach?.contactInfo || '',
                    } as BreachNoticeContent;
                } else if (noticeType === 'ERASURE_NOTICE') {
                    const prevErasure = prevContent as unknown as ErasureNoticeContent;
                    updated[activeLanguage] = {
                        title: result['title'] || prevErasure?.title || '',
                        dataToBeErased: result['dataToBeErased'] || prevErasure?.dataToBeErased || '',
                        reason: result['reason'] || prevErasure?.reason || '',
                        retentionPeriod: result['retentionPeriod'] || prevErasure?.retentionPeriod || '',
                    } as ErasureNoticeContent;
                } else if (noticeType === 'INFORMATIONAL_NOTICE') {
                    const prevInfo = prevContent as unknown as InformationalNoticeContent;
                    updated[activeLanguage] = {
                        title: result['title'] || prevInfo?.title || '',
                        lawfulGround: result['lawfulGround'] || prevInfo?.lawfulGround || '',
                        information: result['information'] || prevInfo?.information || '',
                    } as InformationalNoticeContent;
                }
                return updated;
            });
        } catch (error: any) {
            console.error('Translation error:', error);
            setError(error.response?.data?.message || 'Failed to translate content. Please try again.');
        } finally {
            setIsTranslating(false);
        }
    };

    const handleAcceptTranslation = () => {
        setAcceptedTranslations(prev => {
            const newSet = new Set(prev);
            newSet.add(activeLanguage);
            return newSet;
        });
    };

    const hasTranslationChanges = (): boolean => {
        const current = contentData[activeLanguage];
        const original = originalTranslations[activeLanguage];
        if (!current || !original) return !!current;
        
        if (noticeType === 'BREACH_NOTICE') {
            const curr = current as unknown as BreachNoticeContent;
            const orig = original as unknown as BreachNoticeContent;
            return curr.title !== orig.title ||
                   curr.affectedData !== orig.affectedData ||
                   curr.consequences !== orig.consequences ||
                   curr.measuresTaken !== orig.measuresTaken ||
                   curr.recommendedPrecautions !== orig.recommendedPrecautions ||
                   curr.contactInfo !== orig.contactInfo;
        } else if (noticeType === 'ERASURE_NOTICE') {
            const curr = current as unknown as ErasureNoticeContent;
            const orig = original as unknown as ErasureNoticeContent;
            return curr.title !== orig.title ||
                   curr.dataToBeErased !== orig.dataToBeErased ||
                   curr.reason !== orig.reason ||
                   curr.retentionPeriod !== orig.retentionPeriod;
        } else {
            const curr = current as unknown as InformationalNoticeContent;
            const orig = original as unknown as InformationalNoticeContent;
            return curr.title !== orig.title ||
                   curr.lawfulGround !== orig.lawfulGround ||
                   curr.information !== orig.information;
        }
    };

    const getEnglishContent = () => {
        return contentData['en'] || (noticeType === 'BREACH_NOTICE' ? {
            title: '',
            affectedData: '',
            consequences: '',
            measuresTaken: '',
            recommendedPrecautions: '',
            contactInfo: ''
        } : noticeType === 'ERASURE_NOTICE' ? {
            title: '',
            dataToBeErased: '',
            reason: '',
            retentionPeriod: ''
        } : {
            title: '',
            lawfulGround: '',
            information: ''
        });
    };

    const getCurrentTranslation = () => {
        return contentData[activeLanguage] || (noticeType === 'BREACH_NOTICE' ? {
            title: '',
            affectedData: '',
            consequences: '',
            measuresTaken: '',
            recommendedPrecautions: '',
            contactInfo: ''
        } : noticeType === 'ERASURE_NOTICE' ? {
            title: '',
            dataToBeErased: '',
            reason: '',
            retentionPeriod: ''
        } : {
            title: '',
            lawfulGround: '',
            information: ''
        });
    };

    const MEDIUM_GAP = 24;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: theme.palette.background.paper,
                    minHeight: '600px',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                    {isEditMode ? 'Edit Notice Template' : 'Create Notice Template'}
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 0 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {error && (
                            <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        {/* Tabs */}
                        <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, px: 2 }}>
                            <Tabs
                                value={currentTab}
                                onChange={(_, v) => setCurrentTab(v)}
                                sx={{
                                    '& .MuiTab-root': {
                                        color: theme.palette.text.secondary,
                                        '&.Mui-selected': {
                                            color: theme.palette.primary.main,
                                        },
                                    },
                                }}
                            >
                                <Tab label="Basic Information" />
                                <Tab label="Notice Content" />
                                <Tab label="Translations" />
                            </Tabs>
                        </Box>

                        {/* Tab Panels */}
                        <Box sx={{ flex: 1, overflow: 'auto' }}>
                            {/* Tab 0: Basic Information */}
                            {currentTab === 0 && (
                                <Box sx={{ p: 3 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth error={!!validationErrors.noticeType}>
                                                <InputLabel>Notice Type</InputLabel>
                                                <Select
                                                    value={noticeType}
                                                    label="Notice Type"
                                                    onChange={(e) => handleNoticeTypeChange(e.target.value as NoticeType)}
                                                    disabled={isEditMode}
                                                >
                                                    <MenuItem value="BREACH_NOTICE">Breach Notice</MenuItem>
                                                    <MenuItem value="ERASURE_NOTICE">Erasure Notice</MenuItem>
                                                    <MenuItem value="INFORMATIONAL_NOTICE">Informational Notice</MenuItem>
                                                </Select>
                                                {isEditMode && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                                        Notice type cannot be changed after creation
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Status</InputLabel>
                                                <Select
                                                    value={status}
                                                    label="Status"
                                                    onChange={(e) => setStatus(e.target.value as NoticeStatus)}
                                                >
                                                    <MenuItem value="ACTIVE">Active</MenuItem>
                                                    <MenuItem value="ARCHIVED">Archived</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Title"
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                                error={!!validationErrors.title}
                                                helperText={validationErrors.title}
                                                required
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Description"
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                multiline
                                                rows={3}
                                                placeholder="Brief description of the notice (optional)"
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* Tab 1: Notice Content (English) */}
                            {currentTab === 1 && (
                                <Box sx={{ p: 3 }}>
                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                                        Notice Content (English)
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        Enter the notice content in English. This will be used as the base for translations.
                                    </Typography>

                                    {noticeType === 'BREACH_NOTICE' && (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Title"
                                                    value={(getEnglishContent() as BreachNoticeContent).title || ''}
                                                    onChange={(e) => handleContentChange('title', e.target.value)}
                                                    error={!!validationErrors.contentTitle}
                                                    helperText={validationErrors.contentTitle}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Affected Data"
                                                    value={(getEnglishContent() as BreachNoticeContent).affectedData || ''}
                                                    onChange={(e) => handleContentChange('affectedData', e.target.value)}
                                                    multiline
                                                    rows={3}
                                                    error={!!validationErrors.affectedData}
                                                    helperText={validationErrors.affectedData}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Consequences"
                                                    value={(getEnglishContent() as BreachNoticeContent).consequences || ''}
                                                    onChange={(e) => handleContentChange('consequences', e.target.value)}
                                                    multiline
                                                    rows={3}
                                                    error={!!validationErrors.consequences}
                                                    helperText={validationErrors.consequences}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Measures Taken"
                                                    value={(getEnglishContent() as BreachNoticeContent).measuresTaken || ''}
                                                    onChange={(e) => handleContentChange('measuresTaken', e.target.value)}
                                                    multiline
                                                    rows={3}
                                                    error={!!validationErrors.measuresTaken}
                                                    helperText={validationErrors.measuresTaken}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Recommended Precautions"
                                                    value={(getEnglishContent() as BreachNoticeContent).recommendedPrecautions || ''}
                                                    onChange={(e) => handleContentChange('recommendedPrecautions', e.target.value)}
                                                    multiline
                                                    rows={3}
                                                    error={!!validationErrors.recommendedPrecautions}
                                                    helperText={validationErrors.recommendedPrecautions}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Contact Information"
                                                    value={(getEnglishContent() as BreachNoticeContent).contactInfo || ''}
                                                    onChange={(e) => handleContentChange('contactInfo', e.target.value)}
                                                    multiline
                                                    rows={2}
                                                    error={!!validationErrors.contactInfo}
                                                    helperText={validationErrors.contactInfo}
                                                    required
                                                />
                                            </Grid>
                                        </Grid>
                                    )}

                                    {noticeType === 'ERASURE_NOTICE' && (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Title"
                                                    value={(getEnglishContent() as ErasureNoticeContent).title || ''}
                                                    onChange={(e) => handleContentChange('title', e.target.value)}
                                                    error={!!validationErrors.contentTitle}
                                                    helperText={validationErrors.contentTitle}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Data to be Erased"
                                                    value={(getEnglishContent() as ErasureNoticeContent).dataToBeErased || ''}
                                                    onChange={(e) => handleContentChange('dataToBeErased', e.target.value)}
                                                    multiline
                                                    rows={3}
                                                    error={!!validationErrors.dataToBeErased}
                                                    helperText={validationErrors.dataToBeErased}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Reason for Erasure"
                                                    value={(getEnglishContent() as ErasureNoticeContent).reason || ''}
                                                    onChange={(e) => handleContentChange('reason', e.target.value)}
                                                    multiline
                                                    rows={3}
                                                    error={!!validationErrors.reason}
                                                    helperText={validationErrors.reason}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Retention Period"
                                                    value={(getEnglishContent() as ErasureNoticeContent).retentionPeriod || ''}
                                                    onChange={(e) => handleContentChange('retentionPeriod', e.target.value)}
                                                    multiline
                                                    rows={2}
                                                    error={!!validationErrors.retentionPeriod}
                                                    helperText={validationErrors.retentionPeriod}
                                                    required
                                                />
                                            </Grid>
                                        </Grid>
                                    )}

                                    {noticeType === 'INFORMATIONAL_NOTICE' && (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Title"
                                                    value={(getEnglishContent() as InformationalNoticeContent).title || ''}
                                                    onChange={(e) => handleContentChange('title', e.target.value)}
                                                    error={!!validationErrors.contentTitle}
                                                    helperText={validationErrors.contentTitle}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Lawful Ground"
                                                    value={(getEnglishContent() as InformationalNoticeContent).lawfulGround || ''}
                                                    onChange={(e) => handleContentChange('lawfulGround', e.target.value)}
                                                    multiline
                                                    rows={2}
                                                    error={!!validationErrors.lawfulGround}
                                                    helperText={validationErrors.lawfulGround}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Information"
                                                    value={(getEnglishContent() as InformationalNoticeContent).information || ''}
                                                    onChange={(e) => handleContentChange('information', e.target.value)}
                                                    multiline
                                                    rows={4}
                                                    error={!!validationErrors.information}
                                                    helperText={validationErrors.information}
                                                    required
                                                />
                                            </Grid>
                                        </Grid>
                                    )}
                                </Box>
                            )}

                            {/* Tab 2: Translations */}
                            {currentTab === 2 && showStartTranslation && (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    textAlign: 'center',
                                    p: 4
                                }}>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        Use this space to translate the Notice in multiple languages.<br />
                                        Please ensure that you have finalized the text in English before proceeding.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        sx={{ mb: 2, px: 4 }}
                                        onClick={() => {
                                            setShowStartTranslation(false);
                                            if (languages.length > 0) {
                                                const firstNonEnglishLang = languages.find(lang => lang.code !== 'en');
                                                if (firstNonEnglishLang) {
                                                    setActiveLanguage(firstNonEnglishLang.code);
                                                }
                                            }
                                        }}
                                    >
                                        START TRANSLATION
                                    </Button>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        PS. We provide translation using AI :)
                                    </Typography>
                                </Box>
                            )}

                            {currentTab === 2 && !showStartTranslation && (
                                <Box sx={{ p: 3, display: 'flex', gap: 3, height: `calc(100% - ${MEDIUM_GAP}px)` }}>
                                    {/* Language list */}
                                    <Box sx={{ 
                                        minWidth: 380, 
                                        height: '100%', 
                                        maxHeight: '100%', 
                                        overflowY: 'auto', 
                                        border: `1px solid ${theme.palette.divider}`, 
                                        borderRadius: theme.shape.borderRadius, 
                                        p: 0, 
                                        bgcolor: theme.palette.background.paper, 
                                        display: 'flex', 
                                        flexDirection: 'column' 
                                    }}>
                                        {/* Header Row */}
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            px: 2, 
                                            py: 1, 
                                            borderBottom: `1px solid ${theme.palette.divider}`, 
                                            bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50], 
                                            fontWeight: 600 
                                        }}>
                                            <Typography variant="body2" sx={{ flex: 2, fontWeight: 600, color: theme.palette.text.primary }}>Languages</Typography>
                                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, color: theme.palette.text.primary }}>Code</Typography>
                                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, textAlign: 'center', color: theme.palette.text.primary }}>Accepted</Typography>
                                        </Box>
                                        {/* Language Rows */}
                                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                                            {languages.map((lang) => {
                                                const isSelected = activeLanguage === lang.code;
                                                const isNotSupported = lang.translationProvider === null;
                                                const hasChanges = lang.code !== 'en' && hasTranslationChanges();
                                                const accepted = acceptedTranslations.has(lang.code);
                                                
                                                const rowContent = (
                                                    <Box
                                                        key={lang.code}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            px: 2,
                                                            py: 1,
                                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                                            bgcolor: isSelected ? theme.palette.action.selected : 'inherit',
                                                            opacity: isNotSupported ? 0.5 : 1,
                                                            transition: 'background 0.2s',
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                bgcolor: isNotSupported ? undefined : theme.palette.action.hover,
                                                            },
                                                        }}
                                                        onClick={() => !isNotSupported && setActiveLanguage(lang.code)}
                                                    >
                                                        <Box sx={{ flex: 2 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, display: 'inline', color: theme.palette.text.primary }}>{lang.name}</Typography>
                                                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: theme.palette.text.secondary, display: 'block', ml: 0.5 }}>
                                                                {lang.localeName}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>{lang.code.toUpperCase()}</Typography>
                                                            {hasChanges && (
                                                                <Typography 
                                                                    component="span" 
                                                                    sx={{ 
                                                                        color: theme.palette.primary.main,
                                                                        fontSize: '0.875rem',
                                                                        lineHeight: 1,
                                                                        fontWeight: 600
                                                                    }}
                                                                >
                                                                    •
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                                            <Checkbox
                                                                checked={accepted}
                                                                readOnly
                                                                size="small"
                                                                sx={{ p: 0 }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                );
                                                return isNotSupported ? (
                                                    <Tooltip key={lang.code} title="This language is not supported by AI" arrow>
                                                        <span style={{ display: 'block' }}>{rowContent}</span>
                                                    </Tooltip>
                                                ) : rowContent;
                                            })}
                                        </Box>
                                    </Box>

                                    {/* Translation form for selected language */}
                                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
                                        {/* English Reference */}
                                        <Box sx={{ mb: 2, p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                                            <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                                                English Reference
                                            </Typography>
                                            {noticeType === 'BREACH_NOTICE' && (
                                                <>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                                                        Title: {(getEnglishContent() as BreachNoticeContent).title || ''}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
                                                        Affected Data: {(getEnglishContent() as BreachNoticeContent).affectedData || ''}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
                                                        Consequences: {(getEnglishContent() as BreachNoticeContent).consequences || ''}
                                                    </Typography>
                                                </>
                                            )}
                                            {noticeType === 'ERASURE_NOTICE' && (
                                                <>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                                                        Title: {(getEnglishContent() as ErasureNoticeContent).title || ''}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
                                                        Data to be Erased: {(getEnglishContent() as ErasureNoticeContent).dataToBeErased || ''}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
                                                        Reason: {(getEnglishContent() as ErasureNoticeContent).reason || ''}
                                                    </Typography>
                                                </>
                                            )}
                                            {noticeType === 'INFORMATIONAL_NOTICE' && (
                                                <>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                                                        Title: {(getEnglishContent() as InformationalNoticeContent).title || ''}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
                                                        Lawful Ground: {(getEnglishContent() as InformationalNoticeContent).lawfulGround || ''}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
                                                        Information: {(getEnglishContent() as InformationalNoticeContent).information || ''}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary }}>
                                                Language: {languages.find((l) => l.code === activeLanguage)?.name || activeLanguage}
                                            </Typography>
                                            {activeLanguage !== 'en' && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    endIcon={isTranslating ? <CircularProgress size={20} /> : <img src={AIIcon} alt="AI" style={{ width: 20, height: 20 }} />}
                                                    sx={{
                                                        ml: 2,
                                                        fontWeight: 600,
                                                        color: theme.palette.primary.main,
                                                        borderColor: theme.palette.primary.main,
                                                        '&:hover': {
                                                            borderColor: theme.palette.primary.dark,
                                                            color: theme.palette.primary.dark,
                                                        },
                                                    }}
                                                    disabled={languages.find((l) => l.code === activeLanguage)?.translationProvider === null || isTranslating}
                                                    onClick={handleTranslateAI}
                                                >
                                                    {isTranslating ? 'TRANSLATING...' : 'TRANSLATE USING AI'}
                                                </Button>
                                            )}
                                            <Tooltip
                                                title="AI-generated translations may contain errors or inaccuracies. Please review and cross-check the translation before accepting."
                                                arrow
                                            >
                                                <InfoOutlinedIcon 
                                                    fontSize="small" 
                                                    sx={{ 
                                                        ml: 1, 
                                                        color: theme.palette.text.secondary, 
                                                        cursor: 'pointer',
                                                    }} 
                                                />
                                            </Tooltip>
                                        </Box>

                                        {/* Translation fields based on notice type */}
                                        {noticeType === 'BREACH_NOTICE' && (
                                            <>
                                                <TextField
                                                    label="Title"
                                                    fullWidth
                                                    size="small"
                                                    value={(getCurrentTranslation() as BreachNoticeContent).title || ''}
                                                    onChange={e => handleTranslationChange('title', e.target.value)}
                                                />
                                                <TextField
                                                    label="Affected Data"
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    size="small"
                                                    value={(getCurrentTranslation() as BreachNoticeContent).affectedData || ''}
                                                    onChange={e => handleTranslationChange('affectedData', e.target.value)}
                                                />
                                                <TextField
                                                    label="Consequences"
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    size="small"
                                                    value={(getCurrentTranslation() as BreachNoticeContent).consequences || ''}
                                                    onChange={e => handleTranslationChange('consequences', e.target.value)}
                                                />
                                                <TextField
                                                    label="Measures Taken"
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    size="small"
                                                    value={(getCurrentTranslation() as BreachNoticeContent).measuresTaken || ''}
                                                    onChange={e => handleTranslationChange('measuresTaken', e.target.value)}
                                                />
                                                <TextField
                                                    label="Recommended Precautions"
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    size="small"
                                                    value={(getCurrentTranslation() as BreachNoticeContent).recommendedPrecautions || ''}
                                                    onChange={e => handleTranslationChange('recommendedPrecautions', e.target.value)}
                                                />
                                                <TextField
                                                    label="Contact Information"
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    value={(getCurrentTranslation() as BreachNoticeContent).contactInfo || ''}
                                                    onChange={e => handleTranslationChange('contactInfo', e.target.value)}
                                                />
                                            </>
                                        )}

                                        {noticeType === 'ERASURE_NOTICE' && (
                                            <>
                                                <TextField
                                                    label="Title"
                                                    fullWidth
                                                    size="small"
                                                    value={(getCurrentTranslation() as ErasureNoticeContent).title || ''}
                                                    onChange={e => handleTranslationChange('title', e.target.value)}
                                                />
                                                <TextField
                                                    label="Data to be Erased"
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    size="small"
                                                    value={(getCurrentTranslation() as ErasureNoticeContent).dataToBeErased || ''}
                                                    onChange={e => handleTranslationChange('dataToBeErased', e.target.value)}
                                                />
                                                <TextField
                                                    label="Reason for Erasure"
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    size="small"
                                                    value={(getCurrentTranslation() as ErasureNoticeContent).reason || ''}
                                                    onChange={e => handleTranslationChange('reason', e.target.value)}
                                                />
                                                <TextField
                                                    label="Retention Period"
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    value={(getCurrentTranslation() as ErasureNoticeContent).retentionPeriod || ''}
                                                    onChange={e => handleTranslationChange('retentionPeriod', e.target.value)}
                                                />
                                            </>
                                        )}

                                        {noticeType === 'INFORMATIONAL_NOTICE' && (
                                            <>
                                                <TextField
                                                    label="Title"
                                                    fullWidth
                                                    size="small"
                                                    value={(getCurrentTranslation() as InformationalNoticeContent).title || ''}
                                                    onChange={e => handleTranslationChange('title', e.target.value)}
                                                />
                                                <TextField
                                                    label="Lawful Ground"
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    value={(getCurrentTranslation() as InformationalNoticeContent).lawfulGround || ''}
                                                    onChange={e => handleTranslationChange('lawfulGround', e.target.value)}
                                                />
                                                <TextField
                                                    label="Information"
                                                    fullWidth
                                                    multiline
                                                    rows={4}
                                                    size="small"
                                                    value={(getCurrentTranslation() as InformationalNoticeContent).information || ''}
                                                    onChange={e => handleTranslationChange('information', e.target.value)}
                                                />
                                            </>
                                        )}

                                        {activeLanguage !== 'en' && (
                                            <Button 
                                                variant="contained" 
                                                sx={{ 
                                                    alignSelf: 'flex-start', 
                                                    mt: 2,
                                                    bgcolor: theme.palette.primary.main,
                                                    color: theme.palette.primary.contrastText,
                                                    '&:hover': {
                                                        bgcolor: theme.palette.primary.dark,
                                                    },
                                                }}
                                                onClick={handleAcceptTranslation}
                                                disabled={saving || !hasTranslationChanges()}
                                            >
                                                {saving ? <CircularProgress size={24} color="inherit" /> : 'Accept'}
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={saving || loading}
                >
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>

            {/* Notice Type Change Confirmation Dialog */}
            <ConfirmDialog
                open={showTypeChangeDialog}
                onClose={cancelTypeChange}
                aria-labelledby="type-change-dialog-title"
                aria-describedby="type-change-dialog-description"
            >
                <DialogTitle id="type-change-dialog-title">
                    Change Notice Type?
                </DialogTitle>
                <DialogContent>
                    <Typography id="type-change-dialog-description" sx={{ mb: 2 }}>
                        Changing the notice type will reset all content and translations you've entered.
                    </Typography>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>This action cannot be undone.</strong> All content fields and translations will be lost.
                        </Typography>
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                        Current type: <strong>{noticeType}</strong><br />
                        New type: <strong>{pendingNoticeType}</strong>
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelTypeChange} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={confirmTypeChange} color="error" variant="contained">
                        Change Type & Reset Content
                    </Button>
                </DialogActions>
            </ConfirmDialog>
        </Dialog>
    );
};

export default NoticeModal;
